import React from "react";

const EMO = { attesa: "⏳", confermata: "✓", preparazione: "🍳", consegna: "🚴", completata: "✓", annullata: "✕" };
const LEGEND = [
  ["Da iniziare", "#F59E0B"], ["Confermata", "#60A5FA"], ["In preparazione", "#A78BFA"],
  ["In corso", "#22D3EE"], ["Completata", "#00E676"], ["Annullata", "#EF4444"],
];

// Itinerario gamificato: Partenza 🏠 -> tappe -> Traguardo 🏁, con percorso neon,
// tappa attiva pulsante e stato in tempo reale (i dati arrivano dal polling del dettaglio).
export default function ExperienceMap({ steps = [], allCompleted = false, onOpen }) {
  const nodes = [{ kind: "start" }, ...steps.map((s, i) => ({ kind: "step", idx: i, ...s })), { kind: "finish", done: allCompleted }];
  const W = 360, H = 214, padX = 36, padY = 50, n = nodes.length;
  const pts = nodes.map((nd, i) => ({
    x: n === 1 ? W / 2 : padX + (i * (W - 2 * padX)) / (n - 1),
    y: H / 2 - Math.sin(i * 1.15) * (H / 2 - padY),
  }));
  const completed = steps.filter((s) => s.step_key === "completata").length;
  const boundary = allCompleted ? n - 1 : Math.min(1 + completed, n - 1);
  const activeStepIdx = steps.findIndex((s) => !["completata", "annullata"].includes(s.step_key));
  const activeNode = activeStepIdx >= 0 ? activeStepIdx + 1 : (allCompleted ? n - 1 : -1);
  const baseLine = pts.map((p) => `${p.x},${p.y}`).join(" ");
  const progLine = pts.slice(0, boundary + 1).map((p) => `${p.x},${p.y}`).join(" ");

  const nodeColor = (nd) => nd.kind === "start" ? "#7DF9FF" : nd.kind === "finish" ? (nd.done ? "#00E676" : "#52525B") : (nd.step_color || "#71717A");
  const nodeEmoji = (nd) => nd.kind === "start" ? "🏠" : nd.kind === "finish" ? "🏁" : (EMO[nd.step_key] || "•");
  const nodeLabel = (nd) => nd.kind === "start" ? "Partenza" : nd.kind === "finish" ? "Traguardo" : (nd.business_name || "");

  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden relative bg-[#0B0D12]" data-testid="experience-map">
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
        <defs>
          <radialGradient id="expmap-glow" cx="50%" cy="40%" r="70%">
            <stop offset="0%" stopColor="#12331f" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#0B0D12" stopOpacity="0" />
          </radialGradient>
          <filter id="expmap-neon" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="2.4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <rect x="0" y="0" width={W} height={H} fill="url(#expmap-glow)" />
        {Array.from({ length: 9 }).map((_, i) => (<line key={`v${i}`} x1={i * (W / 8)} y1="0" x2={i * (W / 8)} y2={H} stroke="#ffffff" strokeOpacity="0.04" />))}
        {Array.from({ length: 6 }).map((_, i) => (<line key={`h${i}`} x1="0" y1={i * (H / 5)} x2={W} y2={i * (H / 5)} stroke="#ffffff" strokeOpacity="0.04" />))}

        <polyline points={baseLine} fill="none" stroke="#2A2D36" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1 9" />
        {boundary >= 1 && <polyline points={progLine} fill="none" stroke="#00E676" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" filter="url(#expmap-neon)" />}

        {nodes.map((nd, i) => {
          const p = pts[i]; const col = nodeColor(nd); const isActive = i === activeNode;
          return (
            <g key={i} onClick={() => nd.kind === "step" && onOpen && onOpen(nd)} style={{ cursor: nd.kind === "step" ? "pointer" : "default" }} data-testid={nd.kind === "step" ? `map-node-${nd.idx}` : `map-${nd.kind}`}>
              {isActive && (<circle cx={p.x} cy={p.y} r="16" fill="none" stroke={col} strokeWidth="2" opacity="0.7"><animate attributeName="r" values="14;22;14" dur="1.8s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.7;0;0.7" dur="1.8s" repeatCount="indefinite" /></circle>)}
              <circle cx={p.x} cy={p.y} r="14" fill={`${col}26`} stroke={col} strokeWidth="2" />
              <text x={p.x} y={p.y + 4.5} textAnchor="middle" fontSize="13">{nodeEmoji(nd)}</text>
              <text x={p.x} y={p.y + 30} textAnchor="middle" fontSize="8.5" fill="#D4D4D8" fontWeight="600">{nodeLabel(nd).slice(0, 16)}</text>
              {nd.kind === "step" && nd.step_key === "consegna" && nd.rider_name && (
                <text x={p.x} y={p.y - 20} textAnchor="middle" fontSize="8" fill="#22D3EE">🚴 {String(nd.rider_name).slice(0, 12)}</text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-2 border-t border-white/10">
        {LEGEND.map(([l, c]) => (<span key={l} className="flex items-center gap-1 text-[10px] text-zinc-400"><span className="h-2 w-2 rounded-full" style={{ background: c }} /> {l}</span>))}
      </div>
    </div>
  );
}
